// ═══════════════════════════════════════════════════════════
// PACE Store — Profiles, Plans, Plan Entries, Call-Downs
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { callClaude } from '../lib/api-switch';

const FPP = FIELDS.paceProfiles;
const FPL = FIELDS.pacePlans;
const FPE = FIELDS.pacePlanEntries;
const FCD = FIELDS.callDowns;
const FCDR = FIELDS.callDownResponses;
const FCDT = FIELDS.callDownTemplates;

// ── Types ─────────────────────────────────────────────────

export type PaceTier = 'primary' | 'alternate' | 'contingency' | 'emergency';
export const PACE_TIERS: PaceTier[] = ['primary', 'alternate', 'contingency', 'emergency'];
export const PACE_TIER_LABELS: Record<PaceTier, string> = {
  primary: 'Primary', alternate: 'Alternate', contingency: 'Contingency', emergency: 'Emergency',
};
export const PACE_TIER_COLORS: Record<PaceTier, string> = {
  primary: '#16a34a', alternate: '#2563eb', contingency: '#ca8a04', emergency: '#dc2626',
};

export type CommMethod =
  | 'cell_phone' | 'landline' | 'satellite_phone' | 'radio' | 'email'
  | 'text_sms' | 'signal' | 'teams' | 'zoom' | 'in_person' | 'runners' | 'other';

export const COMM_METHOD_LABELS: Record<CommMethod, string> = {
  cell_phone: 'Cell Phone', landline: 'Landline', satellite_phone: 'Satellite Phone',
  radio: 'Radio / HAM', email: 'Email', text_sms: 'Text / SMS', signal: 'Signal',
  teams: 'Microsoft Teams', zoom: 'Zoom', in_person: 'In-Person Meeting Point',
  runners: 'Runners / Couriers', other: 'Other',
};

export interface PACEProfile {
  id: number;
  incidentId: string;
  profileName: string;
  primary: string;
  alternate: string;
  contingency: string;
  emergency: string;
  status: string;
  notes: string;
  // Extended fields used by PaceTierCard, MyPace, Directory
  tier?: string;           // 'primary' | 'alternate' | 'contingency' | 'emergency'
  method?: string;         // CommMethod value
  contactValue?: string;   // primary contact info (phone, email, freq, etc.)
  backupValue?: string;    // secondary/backup contact info
  lastVerified?: string;   // ISO date last verified
  personName?: string;     // person's name for directory entries
  personEmail?: string;    // person's email
  department?: string;     // department/agency
  functionTitle?: string;  // ICS function / title
}

export interface PACEPlan {
  id: number;
  profileId: string;
  planName: string;
  description: string;
  entriesJson: string;
  creator?: string;
  version?: string;
  acknowledgedBy?: string;
}

export interface PACEPlanEntry {
  id: number;
  planId: string;
  contactName: string;
  contactTitle: string;
  phone: string;
  email: string;
  radioFreq: string;
  method: string;
  sortOrder: string;
  // Extended fields used by Plans.tsx, CommPlans.tsx, PaceTierCard
  contactValue?: string;   // primary contact value (phone/freq/email/url)
  tier?: string;           // 'primary' | 'alternate' | 'contingency' | 'emergency'
  notes?: string;          // per-entry notes
  order?: string;          // display sort order (alias for sortOrder)
}

export interface CallDown {
  id: number;
  profileId: string;
  title: string;
  message: string;
  status: string;
  initiatedBy: string;
  initiatedAt: string;
  totalContacts: number;
  confirmedCount: number;
  // Extended fields used by CallDowns.tsx UI
  memberCount?: number;     // total contacts in the call-down list
  respondedCount?: number;  // number who responded
  scenario?: string;        // call-down scenario/title display
  scope?: string;           // 'org' | 'department'
  callStatus?: string;      // 'active' | 'closed'
  script?: string;          // call script text
  datecreated?: string;     // ISO timestamp or unix seconds from API
}

// ── Mappers ───────────────────────────────────────────────

function mapProfile(r: Record<string, unknown>): PACEProfile {
  return {
    id: r.record_id as number,
    incidentId: String(r[FPP.incidentId] || ''),
    profileName: (r[FPP.profileName] as string) || '',
    primary: (r[FPP.primary] as string) || '',
    alternate: (r[FPP.alternate] as string) || '',
    contingency: (r[FPP.contingency] as string) || '',
    emergency: (r[FPP.emergency] as string) || '',
    status: (r[FPP.status] as string) || 'active',
    notes: (r[FPP.notes] as string) || '',
  };
}

function mapPlan(r: Record<string, unknown>): PACEPlan {
  return {
    id: r.record_id as number,
    profileId: String(r[FPL.profileId] || ''),
    planName: (r[FPL.planName] as string) || '',
    description: (r[FPL.description] as string) || '',
    entriesJson: (r[FPL.entriesJson] as string) || '',
    creator: (r[FPL.creator] as string) || '',
    version: (r[FPL.version] as string) || '',
    acknowledgedBy: (r[FPL.acknowledgedBy] as string) || '',
  };
}

function mapPlanEntry(r: Record<string, unknown>): PACEPlanEntry {
  return {
    id: r.record_id as number,
    planId: String(r[FPE.planId] || ''),
    contactName: (r[FPE.contactName] as string) || '',
    contactTitle: (r[FPE.contactTitle] as string) || '',
    phone: (r[FPE.phone] as string) || '',
    email: (r[FPE.email] as string) || '',
    radioFreq: (r[FPE.radioFreq] as string) || '',
    method: (r[FPE.method] as string) || '',
    sortOrder: (r[FPE.sortOrder] as string) || '0',
  };
}

function mapCallDown(r: Record<string, unknown>): CallDown {
  return {
    id: r.record_id as number,
    profileId: String(r[FCD.profileId] || ''),
    title: (r[FCD.title] as string) || '',
    message: (r[FCD.message] as string) || '',
    status: (r[FCD.status] as string) || 'draft',
    initiatedBy: (r[FCD.initiatedBy] as string) || '',
    initiatedAt: (r[FCD.initiatedAt] as string) || '',
    totalContacts: Number(r[FCD.totalContacts]) || 0,
    confirmedCount: Number(r[FCD.confirmedCount]) || 0,
  };
}

// ── Store ─────────────────────────────────────────────────

interface PACEState {
  profiles: PACEProfile[];
  plans: PACEPlan[];
  planEntries: PACEPlanEntry[];
  callDowns: CallDown[];
  loading: boolean;
  fetchProfiles: () => Promise<void>;
  fetchPlans: () => Promise<void>;
  fetchPlanEntries: (planId?: string) => Promise<void>;
  fetchCallDowns: () => Promise<void>;
  createProfile: (data: Record<string, unknown>) => Promise<number | null>;
  updateProfile: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteProfile: (id: number) => Promise<void>;
  createPlan: (data: Record<string, unknown>) => Promise<number | null>;
  updatePlan: (id: number, data: Record<string, unknown>) => Promise<void>;
  deletePlan: (id: number) => Promise<void>;
  createPlanEntry: (data: Record<string, unknown>) => Promise<number | null>;
  createCallDown: (data: Record<string, unknown>) => Promise<number | null>;
  // V1 compat aliases
  isLoading: boolean;
  loadProfiles: () => Promise<void>;
  loadPlans: () => Promise<void>;
  loadPlanEntries: (planId?: string) => Promise<void>;
  loadCallDowns: (incidentId?: string) => Promise<void>;
  callDownResponses: any[];
  callDownTemplates: any[];
  loadResponses: (callDownId?: string) => Promise<void>;
  loadTemplates: () => Promise<void>;
  initiateCallDown: (data: any) => Promise<number | null>;
  closeCallDown: (id: number) => Promise<void>;
  updateResponse: (id: number, data: any) => Promise<void>;
  createTemplate: (data: any) => Promise<number | null>;
  deleteTemplate: (id: number) => Promise<void>;
  generateCallDownScript: (scenario: string, scope: string) => Promise<string>;
}

export const usePACEStore = create<PACEState>((set, get) => ({
  profiles: [], plans: [], planEntries: [], callDowns: [], loading: false,

  fetchProfiles: async () => {
    try {
      const r = await fetchRecords(TABLES.paceProfiles, { limit: 500 });
      set({ profiles: r.records.map(mapProfile as any) });
    } catch (e) { console.error('[PACE]', e); }
  },

  fetchPlans: async () => {
    try {
      const r = await fetchRecords(TABLES.pacePlans, { limit: 200 });
      set({ plans: r.records.map(mapPlan as any) });
    } catch (e) { console.error('[PACE]', e); }
  },

  fetchPlanEntries: async (planId?: string) => {
    try {
      const r = await fetchRecords(TABLES.pacePlanEntries, { limit: 500 });
      const all: PACEPlanEntry[] = r.records.map(mapPlanEntry as any);
      if (planId) {
        set(s => ({ planEntries: [...s.planEntries.filter(e => e.planId !== planId), ...all.filter(e => String(e.planId) === planId)] }));
      } else {
        set({ planEntries: all });
      }
    } catch (e) { console.error('[PACE]', e); }
  },

  fetchCallDowns: async () => {
    try {
      const r = await fetchRecords(TABLES.callDowns, { limit: 200, sort: { field: 'created_at', direction: 'desc' } });
      set({ callDowns: r.records.map(mapCallDown as any) });
    } catch (e) { console.error('[PACE]', e); }
  },

  createProfile: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.profileName) p[FPP.profileName] = data.profileName;
    if (data.primary) p[FPP.primary] = data.primary;
    if (data.alternate) p[FPP.alternate] = data.alternate;
    if (data.contingency) p[FPP.contingency] = data.contingency;
    if (data.emergency) p[FPP.emergency] = data.emergency;
    if (data.status) p[FPP.status] = data.status;
    if (data.notes) p[FPP.notes] = data.notes;
    if (data.incidentId) p[FPP.incidentId] = data.incidentId;
    try {
      const r = await createRecord<any>(TABLES.paceProfiles, p);
      await get().fetchProfiles();
      return r.record_id ?? r.id ?? null;
    } catch (e) { console.error('[PACE]', e); return null; }
  },

  updateProfile: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.profileName !== undefined) p[FPP.profileName] = data.profileName;
    if (data.primary !== undefined) p[FPP.primary] = data.primary;
    if (data.alternate !== undefined) p[FPP.alternate] = data.alternate;
    if (data.contingency !== undefined) p[FPP.contingency] = data.contingency;
    if (data.emergency !== undefined) p[FPP.emergency] = data.emergency;
    if (data.status !== undefined) p[FPP.status] = data.status;
    if (data.notes !== undefined) p[FPP.notes] = data.notes;
    try {
      await updateRecord(TABLES.paceProfiles, id, p);
      await get().fetchProfiles();
    } catch (e) { console.error('[PACE]', e); }
  },

  deleteProfile: async (id) => {
    try {
      await deleteRecord(TABLES.paceProfiles, id);
      set(s => ({ profiles: s.profiles.filter(p => p.id !== id) }));
    } catch (e) { console.error('[PACE]', e); }
  },

  createPlan: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.planName) p[FPL.planName] = data.planName;
    if (data.description) p[FPL.description] = data.description;
    if (data.entriesJson) p[FPL.entriesJson] = data.entriesJson;
    if (data.profileId) p[FPL.profileId] = data.profileId;
    try {
      const r = await createRecord<any>(TABLES.pacePlans, p);
      await get().fetchPlans();
      return r.record_id ?? r.id ?? null;
    } catch (e) { console.error('[PACE]', e); return null; }
  },

  deletePlan: async (id) => {
    try {
      await deleteRecord(TABLES.pacePlans, id);
      set(s => ({ plans: s.plans.filter(p => p.id !== id) }));
    } catch (e) { console.error('[PACE]', e); }
  },

  createPlanEntry: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.planId) p[FPE.planId] = data.planId;
    if (data.contactName) p[FPE.contactName] = data.contactName;
    if (data.contactTitle) p[FPE.contactTitle] = data.contactTitle;
    if (data.phone) p[FPE.phone] = data.phone;
    if (data.email) p[FPE.email] = data.email;
    if (data.radioFreq) p[FPE.radioFreq] = data.radioFreq;
    if (data.method) p[FPE.method] = data.method;
    if (data.sortOrder) p[FPE.sortOrder] = data.sortOrder;
    try {
      const r = await createRecord<any>(TABLES.pacePlanEntries, p);
      await get().fetchPlanEntries();
      return r.record_id ?? r.id ?? null;
    } catch (e) { console.error('[PACE]', e); return null; }
  },

  createCallDown: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.title) p[FCD.title] = data.title;
    if (data.status) p[FCD.status] = data.status;
    if (data.totalContacts) p[FCD.totalContacts] = data.totalContacts;
    if (data.message) p[FCD.message] = data.message;
    if (data.initiatedBy) p[FCD.initiatedBy] = data.initiatedBy;
    if (data.initiatedAt) p[FCD.initiatedAt] = data.initiatedAt;
    try {
      const r = await createRecord<any>(TABLES.callDowns, p);
      await get().fetchCallDowns();
      return r.record_id ?? r.id ?? null;
    } catch (e) { console.error('[PACE]', e); return null; }
  },

  // V1 compat aliases
  isLoading: false,
  loadProfiles: async () => { await (get() as any).fetchProfiles(); },
  loadPlans: async () => { await (get() as any).fetchPlans(); },
  loadPlanEntries: async (planId?: any) => { await (get() as any).fetchPlanEntries(planId); },
  loadCallDowns: async () => { await (get() as any).fetchCallDowns(); },
  callDownResponses: [] as any[],
  callDownTemplates: [] as any[],
  loadResponses: async (callDownId?: any) => {
    try {
      const r = await fetchRecords<any>(TABLES.callDownResponses, { limit: 200 });
      const responses = r.records.map((row: any) => ({
        id: row.record_id, callDownId: row[FCDR.callDownId],
        contactName: row[FCDR.contactName] || '', contactPhone: row[FCDR.contactPhone] || '',
        contactEmail: row[FCDR.contactEmail] || '', responseStatus: row[FCDR.status] || 'pending',
        respondedAt: row[FCDR.respondedAt] || '', notes: row[FCDR.notes] || '',
      }));
      set({ callDownResponses: callDownId
        ? responses.filter((r: any) => String(r.callDownId) === String(callDownId))
        : responses });
    } catch (e) { console.error('[PACE]', e); }
  },
  loadTemplates: async () => {
    try {
      const r = await fetchRecords<any>(TABLES.callDownTemplates, { limit: 100 });
      set({ callDownTemplates: r.records.map((row: any) => ({
        id: row.record_id, name: row[FCDT.name] || '',
        message: row[FCDT.message] || '', contactListJson: row[FCDT.contactListJson] || '[]',
      })) });
    } catch (e) { console.error('[PACE]', e); }
  },
  initiateCallDown: async (data: any) => {
    const p: Record<string, unknown> = {};
    if (data.title) p[FCD.title] = data.title;
    if (data.message) p[FCD.message] = data.message;
    if (data.initiatedBy) p[FCD.initiatedBy] = data.initiatedBy;
    if (data.totalContacts) p[FCD.totalContacts] = data.totalContacts;
    p[FCD.status] = 'active';
    p[FCD.initiatedAt] = new Date().toISOString();
    try {
      const r = await createRecord<any>(TABLES.callDowns, p);
      await get().fetchCallDowns();
      return r.record_id ?? r.id ?? null;
    } catch (e) { console.error('[PACE]', e); return null; }
  },
  closeCallDown: async (id: any) => {
    try {
      await updateRecord(TABLES.callDowns, id, { [FCD.status]: 'closed' });
      await get().fetchCallDowns();
    } catch (e) { console.error('[PACE]', e); }
  },
  updateResponse: async (id: any, data: any) => {
    try {
      const p: any = {};
      if (data.responseStatus !== undefined) p[FCDR.status] = data.responseStatus;
      if (data.notes !== undefined) p[FCDR.notes] = data.notes;
      if (data.respondedAt !== undefined) p[FCDR.respondedAt] = data.respondedAt;
      await updateRecord(TABLES.callDownResponses, id, p);
      await get().loadResponses();
    } catch (e) { console.error('[PACE]', e); }
  },
  createTemplate: async (data: any) => {
    try {
      const p: any = {};
      if (data.name) p[FCDT.name] = data.name;
      if (data.message) p[FCDT.message] = data.message;
      if (data.contactListJson) p[FCDT.contactListJson] = data.contactListJson;
      const r = await createRecord<any>(TABLES.callDownTemplates, p);
      await get().loadTemplates(); return r.record_id as number;
    } catch (e) { console.error('[PACE]', e); return null; }
  },
  deleteTemplate: async (id: any) => {
    try { await deleteRecord(TABLES.callDownTemplates, id); await get().loadTemplates(); }
    catch (e) { console.error('[PACE]', e); }
  },
  generateCallDownScript: async (scenario: string, scope: string) => {
    const prompt = `You are an emergency management communications specialist. Write a concise call-down script for personnel to read when contacting others during an emergency.

Scenario: ${scenario}
Scope: ${scope === 'org' ? 'Full organization' : 'Department'}

Write a clear, professional script of 3-5 sentences that the caller should read. Include:
- A brief greeting and identification
- The nature of the emergency/situation
- What action is needed from the contact (report availability, report to EOC, etc.)
- A callback number placeholder [CALLBACK NUMBER]

Return only the script text, no preamble or labels.`;
    return await callClaude(prompt, 'You are a concise emergency management communications assistant.');
  },
  saveProfile: async (id: any, data: any) => { await (get() as any).updateProfile(id, data); },
  updatePlan: async (id: any, data: any) => {
    const p: Record<string, unknown> = {};
    if (data.planName !== undefined) p[FPL.planName] = data.planName;
    if (data.description !== undefined) p[FPL.description] = data.description;
    if (data.entriesJson !== undefined) p[FPL.entriesJson] = data.entriesJson;
    if (data.acknowledgedBy !== undefined) p[FPL.acknowledgedBy] = data.acknowledgedBy;
    if (data.creator !== undefined) p[FPL.creator] = data.creator;
    if (data.version !== undefined) p[FPL.version] = data.version;
    try {
      await updateRecord(TABLES.pacePlans, id, p);
      await get().fetchPlans();
    } catch (e) { console.error('[PACE]', e); }
  },
  runGapAnalysis: async () => [],
}));
